import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import { useFeatureFlag } from './useFeatureFlag';

export default function (): {
  hackyRedirectMap: ComputedRef<Record<string, { nonRisk: string; risk: string }>>;
  getRedirectIdentifier: (identifier: string | undefined) => string | undefined;
} {
  const { hasBBVAHackyRedirectMap, hasSimplexHackyRedirectMap, hasBIAHackyRedirectMap } = useFeatureFlag();

  /**
   * Until the portfolio tree is restructured so that a portfolio tree may have both risk data
   * and a custom weighting, we are implementing a hacky map such that if Simplex users
   * are on their risk portfolio and go to Portfolio Construction, they will see their
   * custom weighting portfolio. And vice versa, if they click on Risk.
   */
  const hackySimplexRedirectMap: Record<string, { nonRisk: string; risk: string }> = {
    'cross-asset-basket-strategy': {
      nonRisk: 'cross-asset-basket-strategy-wnwWU',
      risk: 'cross-asset-basket-strategy-iaYSy',
    },
    'simplex-multi-carry-strategy': {
      nonRisk: 'simplex-multi-carry-strategy-22f01f01',
      risk: 'simplex-multi-carry-strategy-d969f54c',
    },
    'commodity-multi-carry-strategy-fund-for-qii-610': {
      nonRisk: 'commodity-multi-carry-strategy-fund-for-qii-610-a8538d6a',
      risk: 'commodity-multi-carry-strategy-fund-for-qii-610-f080ee68',
    },
    'commodity-multi-carry-strategy-fund-for-qii-523': {
      nonRisk: 'commodity-multi-carry-strategy-fund-for-qii-523-627d4f90',
      risk: 'commodity-multi-carry-strategy-fund-for-qii-523-a7938a3f',
    },
    'enhanced-multi-asset-strategy-fund': {
      nonRisk: 'enhanced-multi-asset-strategy-fund-e88b750f',
      risk: 'enhanced-multi-asset-strategy-fund-a8b1ea32',
    },
  };

  /**
   * Until the portfolio tree is restructured so that a portfolio tree may have both risk data
   * and a custom weighting, we are implementing a hacky map such that if BBVA users
   * are on their risk portfolio and go to Portfolio Construction, they will see their
   * custom weighting portfolio. And vice versa, if they click on Risk.
   */
  const hackyBBVARedirectMap: Record<string, { nonRisk: string; risk: string }> = {
    'bbva-betaminer': {
      nonRisk: 'bbva-betaminer-205e9590',
      risk: 'bbva-betaminer-9c3f5daa',
    },
    'bbva-behedged-ms': {
      nonRisk: 'bbva-behedged-ms-6f17326f',
      risk: 'bbva-behedged-ms-8a9efa21',
    },
    'bbva-behedged-gs': {
      nonRisk: 'bbva-behedged-gs-eabd03e0',
      risk: 'bbva-behedged-gs-78eaabda',
    },
    'bbva-behedged-portfolio': {
      nonRisk: 'bbva-behedged-portfolio-639d419e',
      risk: 'bbva-behedged-portfolio-e1953c53',
    },
    'bbva-ubs': {
      nonRisk: 'bbva-ubs-d366da9f',
      risk: 'bbva-ubs-115e5bda',
    },
    'bbva-barclays': {
      nonRisk: 'bbva-barclays-d4e52567',
      risk: 'bbva-barclays-00ff0ec6',
    },
    'bbva-nomura-trend-basket': {
      nonRisk: 'bbva-nomura-trend-basket-22f7a3a5',
      risk: 'bbva-nomura-trend-basket-bfd150e4',
    },
    'bbva-nomura-carry-basket': {
      nonRisk: 'bbva-nomura-carry-basket-a563601f',
      risk: 'bbva-nomura-carry-basket-d3fbdef6',
    },
    'bbva-nomura-value-basket': {
      nonRisk: 'bbva-nomura-value-basket-c6b0e03a',
      risk: 'bbva-nomura-value-basket-394e8887',
    },
    DBCUSBM1: {
      nonRisk: 'bbva-betaminer-205e9590',
      risk: 'DBCUSBM1',
    },
    MSCBCDEF: {
      nonRisk: 'bbva-behedged-ms-6f17326f',
      risk: 'MSCBCDEF',
    },
    GSISI127: {
      nonRisk: 'bbva-behedged-gs-eabd03e0',
      risk: 'GSISI127',
    },
  };

  /**
   * Until the portfolio tree is restructured so that a portfolio tree may have both risk data
   * and a custom weighting, we are implementing a hacky map such that if BIA users
   * are on their risk portfolio and go to Portfolio Construction, they will see their
   * custom weighting portfolio. And vice versa, if they click on Risk.
   */
  const hackyBIARedirectMap: Record<string, { nonRisk: string; risk: string }> = {
    'bia-overlay-portfolio': {
      nonRisk: 'bia-overlay-portfolio-891760ce',
      risk: 'bia-overlay-portfolio-3e67b870',
    },
  };

  const allTextBeforeLastHyphenInclusive = /.*-/;

  /**
   * Prior to Sep 2025, this function only handled portfolio slugs (which always contain hyphens).
   * Now, if it is fed an identifier without matching this format, it will return the identifier as is, allowing the same
   * logic to work if using a live strategy (shouldExposePositions = true).
   */
  const getRedirectIdentifier = (identifier: string | undefined): string | undefined => {
    const arr = identifier?.match(allTextBeforeLastHyphenInclusive);
    if (arr && arr.length) {
      return arr[0].substring(0, arr[0].length - 1) ?? null;
    }

    return identifier;
  };

  const hackyRedirectMap = computed(() => {
    if (hasBBVAHackyRedirectMap.value) {
      return hackyBBVARedirectMap;
    }

    if (hasSimplexHackyRedirectMap.value) {
      return hackySimplexRedirectMap;
    }

    if (hasBIAHackyRedirectMap.value) {
      return hackyBIARedirectMap;
    }

    return {};
  });

  return {
    hackyRedirectMap,
    getRedirectIdentifier,
  };
}
